import { boxConstants, tagConstants } from '../_constants';
import { boxService } from '../_services';
import { boxUtilities, readBoxCount, writeBoxCount } from '../_utilities';
import { alertActions, stackActions } from './';
import { normalize, schema } from 'normalizr';
import { store } from '../store';
import { mark } from '../../perf';


export const boxActions = {
  select,
  load,
  sort,
  floatTagged,
  shuffle,
  addOrigami,
  deleteOrigami,
  addLink,
  deleteLink,
  moveUp,
  moveDown
};


// Normalized data schema
const tag = new schema.Entity('tags');      // linkId stored
const link = new schema.Entity('links', {
  stack: tag
});
const origami = new schema.Entity('origamis', {
  origamiLinks: {
    items: [link]
  }
});


function moveUp(){
  const { allIds } = store.getState().box.origamis;
  const { selected } = store.getState().box.status;
  const indexOf = allIds.findIndex(i => i === selected);

  if (indexOf !== 0 && indexOf !== null) {
    return(select(allIds[indexOf - 1]));
  }
  return { type: boxConstants.SELECT, id: selected };
}


function moveDown() {
  const { allIds } = store.getState().box.origamis;
  const { selected } = store.getState().box.status;
  const indexOf = allIds.findIndex(i => i === selected);

  if (indexOf !== (allIds.length - 1) && indexOf !== null) {
    return(select(allIds[indexOf + 1]));
  }  
  return { type: boxConstants.SELECT, id: selected };
}


function select(id) {
  return { type: boxConstants.SELECT, id };
}


function floatTagged(tagId, allIds, byId, linksById) {
  const result = boxUtilities.floatTagged(tagId, allIds, byId, linksById);
  return { type: boxConstants.FLOAT_TAGGED, result };
}


// Returns a new byId map with one origami's link list replaced. These used to be
// mutated in place, which left the state object identical by reference — so
// react-redux saw no change and components did not re-render reliably.
function withLinkItems(byId, origamiId, items) {
  const origami = byId[origamiId];

  return {
    ...byId,
    [origamiId]: {
      ...origami,
      origamiLinks: { ...origami.origamiLinks, items },
    },
  };
}


function addLink(link) {
  const { byId } = store.getState().box.origamis;
  const items = byId[link.origamiId].origamiLinks.items;

  if (link.id === tagConstants.TAG_REQUEST) {
    return {
      type: boxConstants.ADD_LINK_PENDING,
      byId: withLinkItems(byId, link.origamiId, [...items, link.id]),
    };
  }

  // Replace the optimistic placeholder with the id the server assigned.
  const index = items.indexOf(tagConstants.TAG_REQUEST);
  if (index === -1) {
    return { type: boxConstants.ADD_LINK, byId };
  }

  const nextItems = [...items];
  nextItems[index] = link.id;

  return {
    type: boxConstants.ADD_LINK,
    byId: withLinkItems(byId, link.origamiId, nextItems),
  };
}


function deleteLink(link) {
  const { byId } = store.getState().box.origamis;
  const newTags = byId[link.origamiId].origamiLinks.items.filter(id => id !== link.id);
  return {
    type: boxConstants.DELETE_LINK,
    byId: withLinkItems(byId, link.origamiId, newTags),
  };
}


function shuffle(allIds) {
  return dispatch => {
    dispatch(request());
    const result = boxUtilities.shuffle(allIds);
    dispatch(success(result));
  }
  
  function request() { return { type: boxConstants.LOADING } };
  function success(result) { return { type: boxConstants.SHUFFLE, result } };
}


function sort(sortBy, allIds, byId) {
  const result = boxUtilities.sort(sortBy, allIds, byId);
  return { type: sortBy, result };
}


function addOrigami(data) {
  const { allIds, byId } = store.getState().box.origamis;

  const newAllIds = [ data.id, ...allIds ];
  const newOrigami = {};
  newOrigami[data.id] = data;
  const newById = { ...byId, ...newOrigami };

  return { type: boxConstants.ADD_ORIGAMI, origamis: { allIds: newAllIds, byId: newById }, id: data.id };
}


function deleteOrigami(id, nextId) {
  // Remove from box and mark origami deleted
  // Mark all links that reference this origami deleted
  return dispatch => {
    const { allIds, byId } = store.getState().box.origamis;
    const newAllIds = allIds.filter(i => i !== id);
    const og = byId[id];
    // `delete byId[id]` followed by `Object.assign(byId)` mutated the store and
    // handed back the same reference. Build a new map instead.
    const { [id]: removedOrigami, ...newByIds } = byId;
    const data = { byId: newByIds, allIds: newAllIds, nextId: nextId };
    dispatch(request(data));
    dispatch(stackActions.deleteLinksForOrigami(og));

    boxService.deleteOG(id)                               
    .then(res => {  
      dispatch(success());
    })
    .catch(error => { 
      dispatch(failure(error.toString()));
      dispatch(alertActions.error(error.toString()));
    })
  };

  function request(data) { return { type: boxConstants.DELETE_OG_REQUEST, data } };
  function success() { return { type: boxConstants.DELETE_OG_SUCCESS } };
  function failure(error) { return { type: boxConstants.DELETE_OG_FAILURE } };
}


function load(username) {
  return dispatch => {
    dispatch(request());

    const cached = readBoxCount(username);
    let first = true;
    let totalDecided = false;
    boxService.load(username, (pageItems) => {
      // Each page lands in the store as it arrives. The first one is enough
      // to pick a selection and start the first model loading, so the user is
      // no longer waiting on every remaining page before anything appears.
      const isFirst = first;
      if (isFirst) {
        mark('box:first-page');
        first = false;
      }
      // `first` rides on the action so the reducers can replace on the first
      // page and append on the rest without reading each other's state.
      const data = { origamis: normalize(pageItems, [origami]), first: isFirst };
      // Decided on the first page that has any items (a small page can come
      // back empty when its rows are all deleted ones): the cached total holds
      // only if the newest scan is still the one it was recorded against.
      if (!totalDecided && pageItems.length > 0) {
        totalDecided = true;
        data.expectedTotal =
          cached && cached.newestId === pageItems[0].id ? cached.total : null;
      }
      dispatch(page(data));
      // react-redux renders synchronously inside dispatch, so the gap from
      // this page's `page:` mark to this one is what rendering it cost.
      mark(`box:page:rendered:${pageItems.length}`);
    })
    .then((items) => {
      mark('box:load:success');
      dispatch(complete());
      if (items && items.length > 0) {
        writeBoxCount(username, items.length, items[0].id);
      }
    })
    .catch(error => {
      dispatch(failure(error.toString()));
      dispatch(alertActions.error(error.toString()));
    })
  };

  function request() { return { type: boxConstants.LOAD_REQUEST } };
  function page(data) { return { type: boxConstants.LOAD_PAGE, data } };
  function complete() { return { type: boxConstants.LOAD_COMPLETE } };
  function failure(error) { return { type: boxConstants.LOAD_FAILURE } };
}